import { IS_PRODUCTION } from 'gatsby-theme-hem-store/src/constants';
import { persist } from 'zustand/middleware';
import { shallow } from 'zustand/shallow';
import { createWithEqualityFn } from 'zustand/traditional';

import { fetchCustomer } from './fetchCustomer';
import { login } from './login';
import { loginMultipass } from './loginMultipass';
import { logout } from './logout';
import { renewCustomerAccessToken } from './renewCustomerAccessToken';
import { resetPassword } from './resetPassword';

export type Customer = {
  id: string;
  createdAt: Date;
  updatedAt: Date;
  // The customer’s name, email or phone number.
  displayName: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  email?: string;
  tags: string[];
  defaultAddress: {
    address1?: string;
    address2?: string;
    city?: string;
    zip?: string;
    country?: string;
    company?: string;
  };
};

type CustomerAccessToken = {
  accessToken: string;
  expiresAt: Date;
};

export type CustomerStore = {
  customer?: Customer;
  customerAccessToken?: CustomerAccessToken;
  customerLoginError?: string;
  customerResetPasswordError?: string;
  setCustomerResetPasswordError: (string: string) => void;
  currentProDiscount?: string;
  setCustomerLoginError: (string: string) => void;
  login: (input: { email: string; password: string }) => Promise<void>;
  loginMultipass: (input: { email: string }) => Promise<void>;
  renewCustomerAccessToken: () => Promise<void>;
  logout: () => Promise<void>;
  resetPassword: (input: { email: string }) => Promise<void>;
  ___fetchCustomer: () => Promise<void>;
  setCurrentProDiscount: (discount: string) => void;
};

export const useCustomer = createWithEqualityFn<CustomerStore>()(
  persist(
    (set, get) => ({
      customer: undefined,
      customerAccessToken: undefined,
      customerLoginError: undefined,
      setCustomerLoginError: (customerLoginError) => {
        set({ customerLoginError });
      },
      customerResetPasswordError: undefined,
      setCustomerResetPasswordError: (customerResetPasswordError) => {
        set({ customerResetPasswordError });
      },
      currentProDiscount: undefined,
      login: login(set, get),
      loginMultipass: loginMultipass(set, get),
      renewCustomerAccessToken: renewCustomerAccessToken(set, get),
      logout: logout(set, get),
      resetPassword: resetPassword(set),
      ___fetchCustomer: fetchCustomer(set, get),
      setCurrentProDiscount: (currentProDiscount) => {
        set({ currentProDiscount });
      },
    }),
    {
      name: IS_PRODUCTION
        ? `hem-customer-storage-${process.env.GATSBY_REGION_REGION_DIFFERENTIATOR}`
        : `hem-development-customer-storage-${process.env.GATSBY_REGION_REGION_DIFFERENTIATOR}`
            .toLowerCase()
            .replace(' ', '-'),
      partialize: (state) => ({
        customer: state.customer,
        customerAccessToken: state.customerAccessToken,
        currentProDiscount: state.currentProDiscount,
      }),
    },
  ),
  shallow,
);
